<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useLocale } from 'shared/composables/useLocale';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import MasifiWhatsappQrAPI from 'dashboard/api/masifi/masifiWhatsappQr';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  WHATSAPP_QR_EVENT,
  WHATSAPP_QR_FEATURE,
  WHATSAPP_QR_HISTORY_EVENT,
  pickNumberSettings,
} from 'dashboard/helper/masifi/masifiWhatsappQr';
import MasifiWaNumberSettings from './MasifiWaNumberSettings.vue';

// Panel de un número de WhatsApp por QR: checklist + aceptación de riesgo, QR en vivo, estado, pausar/reanudar,
// desvincular. Admin-only (la API lo es). Spec: whatsapp-qr-design.md §3.2/§5.1/§6.1-7.
const props = defineProps({ inbox: { type: Object, required: true } });
const emit = defineEmits(['connected']);
const { t } = useI18n();

// Capa 1 del gate: con el módulo apagado se ocultan las ACCIONES (nada llega al conector), pero `show` es de
// solo lectura (#396) y el backend ahora la deja pasar apagado (E2E fix #8, Ruling en EnsureWhatsappQrEnabled)
// porque el estado es metadata del canal — el panel lo lee igual para explicarse en vez de un cartel vacío.
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const moduleEnabled = computed(() =>
  isFeatureEnabled.value(accountId.value, WHATSAPP_QR_FEATURE)
);
const { isAdmin } = useAdmin();

const state = ref({ connection: 'unlinked', risk_accepted: false });
const qr = ref(null);
// Fase 2 (spec §3.1): el código de vinculación (efímero; llega por el payload o el broadcast, solo a admins).
const pairingCode = ref(null);
// El modo pedido en este connect: mientras el código no llega se avisa que se genera (no la ayuda del QR).
const requestedCode = ref(false);
const riskChecked = ref(false);
const busy = ref(false);
const unlinkDialogRef = ref(null);

// Después de `state` (no-use-before-define aplica también en closures — lesson-repo-lint-hook-pitfalls).
// Fechas en el idioma de la UI con el patrón nativo (useLocale + Intl, como AccountHealth.vue) — revisión E2E fix 1, M5:
// date-fns sin locale escribía "Sep 24, 2026" en la UI en español.
const { resolvedLocale } = useLocale();
const localDateTime = value =>
  value
    ? new Intl.DateTimeFormat(resolvedLocale.value, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';
const banUntil = computed(() => localDateTime(state.value.ban_until));
// Anti-baneo R5 (auditoría 2026-09-24): WhatsApp limitó las consultas del número (grupos/fotos) hasta esa hora.
const queriesPausedUntil = computed(() =>
  localDateTime(state.value.queries_paused_until)
);

const GREEN = ['open'];
const AMBER = ['pairing', 'connecting', 'closed', 'paused', 'unlinking'];
const tone = computed(() => {
  if (GREEN.includes(state.value.connection)) return 'text-n-teal-11';
  if (AMBER.includes(state.value.connection)) return 'text-n-amber-11';
  return 'text-n-ruby-11';
});
const canConnect = computed(() =>
  ['unlinked', 'closed', 'paused', 'logged_out', 'failed', 'replaced'].includes(
    state.value.connection
  )
);
const canPause = computed(() =>
  ['open', 'connecting', 'closed'].includes(state.value.connection)
);
// Second-pass review C2: connector v0.3.0 carries a session.logout only over a live socket (it never dials for
// it, and a session it is not running never reads it). So Unlink is offered only while a paired number is
// connected; a pairing that never completed is cancelled instead, and every other paired state gets a hint
// (resume first / remove it from the phone) rather than a button that can only fail again.
const paired = computed(() => Boolean(state.value.paired_at));
const canUnlink = computed(
  () => paired.value && ['open', 'connecting'].includes(state.value.connection)
);
const canCancelPairing = computed(() => state.value.connection === 'pairing');
const unlinkHint = computed(() => {
  const { connection } = state.value;
  if (
    !paired.value ||
    ['open', 'connecting', 'unlinking', 'unlinked', 'logged_out'].includes(
      connection
    )
  )
    return '';
  return connection === 'paused'
    ? t('MASIFI_WHATSAPP_QR.UNLINK_HINT_PAUSED')
    : t('MASIFI_WHATSAPP_QR.UNLINK_HINT_PHONE');
});
const errorText = computed(() => {
  const code = state.value.last_error?.code;
  return code
    ? t(
        `MASIFI_WHATSAPP_QR.ERRORS.${code.toUpperCase()}`,
        t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC')
      )
    : '';
});

// Fase 2: la vinculación muestra UNO de los dos — el QR o el código (el último que llegó); fuera de `pairing`, ninguno.
const apply = data => {
  state.value = { ...state.value, ...data };
  if (data.connection !== 'pairing') {
    qr.value = null;
    pairingCode.value = null;
    requestedCode.value = false;
  } else if (data.pairing_code) {
    pairingCode.value = data.pairing_code;
    qr.value = null;
  } else if (data.qr_data_url) {
    qr.value = data.qr_data_url;
    pairingCode.value = null;
  }
  if (data.connection === 'open') emit('connected');
};

const run = async action => {
  busy.value = true;
  try {
    const { data } = await action();
    apply(data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC')
    );
  } finally {
    busy.value = false;
  }
};

const connect = () => {
  requestedCode.value = false;
  return run(() =>
    MasifiWhatsappQrAPI.connect(props.inbox.id, {
      riskAccepted: riskChecked.value,
    })
  );
};
// Fase 2 (spec §3.1): vincular con el número del inbox (código de 8 caracteres), solo un número que nunca se vinculó (uno
// vinculado se reanuda). El backend topa los pedidos (429 con el texto de espera, que muestra `run`).
const canConnectWithCode = computed(() => canConnect.value && !paired.value);
const connectWithCode = () => {
  pairingCode.value = null; // nunca mostrar un código vencido mientras llega el nuevo
  requestedCode.value = true;
  return run(() =>
    MasifiWhatsappQrAPI.connect(props.inbox.id, {
      riskAccepted: riskChecked.value,
      pairing: 'code',
    })
  );
};
const copyCode = async () => {
  try {
    await copyTextToClipboard(pairingCode.value);
    useAlert(t('MASIFI_WHATSAPP_QR.PAIRING_CODE.COPIED'));
  } catch (error) {
    useAlert(t('MASIFI_WHATSAPP_QR.PAIRING_CODE.COPY_FAILED'));
  }
};
const pause = () => run(() => MasifiWhatsappQrAPI.pause(props.inbox.id));
// Rule 6 (native components, no window.confirm): the native Dialog asks for confirmation; the API call
// only happens on its `confirm` event (F5, final review).
const unlink = () => unlinkDialogRef.value.open();
// E2E fix #1(a) (Marcos, live test 2026-09-23): confirmUnlink never closed the Dialog, so a user who saw no
// feedback clicked "Desvincular" again on what looked like a stuck dialog, firing a second session.logout
// while the first was still in flight. Close it up front (the panel's own `busy` state, already gating every
// action button, keeps a second click from reopening it before the request resolves).
const confirmUnlink = () => {
  unlinkDialogRef.value.close();
  run(() => MasifiWhatsappQrAPI.unlink(props.inbox.id));
};
// Nothing is linked yet, so there is nothing to confirm: the backend cancels the pairing run (C1).
const cancelPairing = () =>
  run(() => MasifiWhatsappQrAPI.unlink(props.inbox.id));

// Ajustes por número (spec polish-1 §4; antes F2 ruling 13 + F3a): los guarda MasifiWaNumberSettings. Se muestran cuando el
// payload de la sesión ya los trae (la sesión existe) y con el módulo encendido (el PATCH pasa por el gate). Acá solo se
// refleja lo guardado — sin `apply`, que re-emitiría `connected` (el paso final del alta avanza con eso).
const hasSettings = computed(() => typeof state.value.groups === 'boolean');
const numberSettings = computed(() => pickNumberSettings(state.value));
const applySettings = data => {
  state.value = { ...state.value, ...pickNumberSettings(data) };
};

useEmitter(WHATSAPP_QR_EVENT, data => {
  if (data.inbox_id === props.inbox.id) apply(data);
});

// Fase 2 (spec §5.5): cómo va el historial del teléfono de este número (History::Progress; solo admins lo reciben) y, antes
// de vincular con la importación prendida, qué se va a importar.
const historyText = computed(() => {
  const history = state.value.history;
  if (!history?.state) return '';
  const imported = history.imported || 0;
  if (history.state === 'done') {
    return t('MASIFI_WHATSAPP_QR.HISTORY.DONE', { imported });
  }
  if (history.state === 'importing') {
    const total = Math.max(
      (history.received || 0) - (history.skipped || 0),
      imported
    );
    return t('MASIFI_WHATSAPP_QR.HISTORY.IMPORTING', { imported, total });
  }
  if (history.state === 'receiving') {
    return t('MASIFI_WHATSAPP_QR.HISTORY.RECEIVING', {
      count: history.received || 0,
    });
  }
  return '';
});
const historyPairingNote = computed(() =>
  state.value.history_available &&
  state.value.history_import &&
  canConnectWithCode.value
    ? t('MASIFI_WHATSAPP_QR.HISTORY.PAIRING_NOTE', {
        days: state.value.history_days,
      })
    : ''
);
useEmitter(WHATSAPP_QR_HISTORY_EVENT, data => {
  if (data.inbox_id === props.inbox.id) {
    state.value = { ...state.value, history: data.history };
  }
});

onMounted(() => {
  run(() => MasifiWhatsappQrAPI.get(props.inbox.id));
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm font-medium" :class="tone">
      {{ t(`MASIFI_WHATSAPP_QR.STATE.${state.connection.toUpperCase()}`) }}
    </p>
    <!-- E2E fix #8 (Marcos, live test 2026-09-23): with the module off, the real (read-only) state above still
    shows (e.g. "Pausado" once the gate paused it) -- this just explains why, and points an admin at the knob. -->
    <p
      v-if="!moduleEnabled"
      data-test="wa-qr-module-off"
      class="text-sm text-n-slate-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.MODULE_OFF_HINT') }}
      <router-link
        v-if="isAdmin"
        :to="{ name: 'masifi_settings_index', params: { accountId } }"
        class="underline"
      >
        {{ t('MASIFI_WHATSAPP_QR.MODULE_OFF_LINK') }}
      </router-link>
    </p>
    <p
      v-if="banUntil"
      data-test="wa-qr-ban-until"
      class="text-sm text-n-slate-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.BAN_UNTIL', { date: banUntil }) }}
    </p>
    <p
      v-if="queriesPausedUntil"
      data-test="wa-qr-queries-paused"
      class="text-sm text-n-amber-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.QUERIES_PAUSED', { date: queriesPausedUntil }) }}
    </p>
    <p v-if="errorText" class="text-sm text-n-slate-11">{{ errorText }}</p>
    <p
      v-if="historyText"
      data-test="wa-qr-history"
      class="text-sm text-n-slate-11"
    >
      {{ historyText }}
    </p>
    <p
      v-if="moduleEnabled && unlinkHint"
      data-test="wa-qr-unlink-hint"
      class="text-sm text-n-slate-11"
    >
      {{ unlinkHint }}
    </p>
    <!-- E2E fix #9 (connector investigation 2026-09-23): connector v0.3.0 never acks the phone's history
    sync, so the device can keep showing "Sincronizando" in the phone's own Linked devices list even after a
    real unlink (upstream connector limitation). Point the admin at the phone-side fix. -->
    <p
      v-if="state.connection === 'logged_out'"
      data-test="wa-qr-sync-hint"
      class="text-sm text-n-slate-11"
    >
      {{ t('MASIFI_WHATSAPP_QR.SYNC_HINT') }}
    </p>

    <!-- E2E fix #8: no actions while the module is off -- checklist, QR and the connect/pause/unlink buttons
    all stay gated (layer 1), matching the backend (only `show` is allowed through layer 3 when off). -->
    <template v-if="moduleEnabled">
      <div
        v-if="!state.risk_accepted"
        data-test="wa-qr-risk"
        class="flex flex-col gap-2 text-sm text-n-slate-12"
      >
        <p class="font-medium">{{ t('MASIFI_WHATSAPP_QR.RISK.TITLE') }}</p>
        <ul class="list-disc ps-5 text-n-slate-11">
          <li
            v-for="item in [
              'TOS',
              'BUSINESS',
              'AGE',
              'PROFILE',
              'PHONE_ONLINE',
              'NO_OTHER_TOOL',
            ]"
            :key="item"
          >
            {{ t(`MASIFI_WHATSAPP_QR.RISK.${item}`) }}
          </li>
        </ul>
        <label class="flex items-center gap-2">
          <Checkbox v-model="riskChecked" />
          {{ t('MASIFI_WHATSAPP_QR.RISK.ACCEPT') }}
        </label>
      </div>

      <img
        v-if="qr"
        data-test="wa-qr-image"
        :src="qr"
        :alt="t('MASIFI_WHATSAPP_QR.QR_ALT')"
        class="size-64"
      />
      <p
        v-if="state.connection === 'pairing' && !pairingCode && !qr"
        data-test="wa-qr-code-generating"
        class="text-sm text-n-slate-11"
      >
        {{
          requestedCode
            ? t('MASIFI_WHATSAPP_QR.PAIRING_CODE.GENERATING')
            : t('MASIFI_WHATSAPP_QR.SCAN_HELP')
        }}
      </p>
      <p
        v-else-if="state.connection === 'pairing' && qr"
        class="text-sm text-n-slate-11"
      >
        {{ t('MASIFI_WHATSAPP_QR.SCAN_HELP') }}
      </p>
      <div
        v-if="pairingCode"
        data-test="wa-qr-pairing-code"
        class="flex flex-col gap-2"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('MASIFI_WHATSAPP_QR.PAIRING_CODE.LABEL') }}
        </p>
        <p class="font-mono text-3xl tracking-widest text-n-slate-12">
          {{ pairingCode }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{
            t('MASIFI_WHATSAPP_QR.PAIRING_CODE.HELP', {
              phone: inbox.phone_number,
            })
          }}
        </p>
        <div class="flex flex-wrap gap-2">
          <NextButton
            slate
            faded
            icon="i-lucide-copy"
            data-test="wa-qr-pairing-code-copy"
            :label="t('MASIFI_WHATSAPP_QR.PAIRING_CODE.COPY')"
            @click="copyCode"
          />
          <NextButton
            slate
            data-test="wa-qr-pairing-code-again"
            :label="t('MASIFI_WHATSAPP_QR.PAIRING_CODE.AGAIN')"
            :is-loading="busy"
            :disabled="busy"
            @click="connectWithCode"
          />
        </div>
      </div>

      <p
        v-if="historyPairingNote"
        data-test="wa-qr-history-note"
        class="text-sm text-n-slate-11"
      >
        {{ historyPairingNote }}
      </p>
      <div data-test="wa-qr-actions" class="flex flex-wrap gap-2">
        <NextButton
          v-if="canConnect"
          :label="
            t(
              state.connection === 'paused'
                ? 'MASIFI_WHATSAPP_QR.RESUME'
                : 'MASIFI_WHATSAPP_QR.CONNECT'
            )
          "
          :is-loading="busy"
          :disabled="busy || (!state.risk_accepted && !riskChecked)"
          @click="connect"
        />
        <NextButton
          v-if="canConnectWithCode"
          slate
          data-test="wa-qr-connect-code"
          :label="t('MASIFI_WHATSAPP_QR.PAIRING_CODE.CONNECT')"
          :is-loading="busy"
          :disabled="busy || (!state.risk_accepted && !riskChecked)"
          @click="connectWithCode"
        />
        <NextButton
          v-if="canPause"
          slate
          :label="t('MASIFI_WHATSAPP_QR.PAUSE')"
          :disabled="busy"
          @click="pause"
        />
        <NextButton
          v-if="canUnlink"
          ruby
          faded
          data-test="wa-qr-unlink"
          :label="t('MASIFI_WHATSAPP_QR.UNLINK')"
          :disabled="busy"
          @click="unlink"
        />
        <NextButton
          v-if="canCancelPairing"
          slate
          faded
          data-test="wa-qr-cancel-pairing"
          :label="t('MASIFI_WHATSAPP_QR.CANCEL_PAIRING')"
          :disabled="busy"
          @click="cancelPairing"
        />
      </div>

      <MasifiWaNumberSettings
        v-if="hasSettings"
        :inbox-id="inbox.id"
        :settings="numberSettings"
        :history-available="Boolean(state.history_available)"
        @saved="applySettings"
      />
    </template>

    <Dialog
      ref="unlinkDialogRef"
      type="alert"
      :title="t('MASIFI_WHATSAPP_QR.UNLINK_DIALOG_TITLE')"
      :description="`${t('MASIFI_WHATSAPP_QR.UNLINK_CONFIRM')} ${t('MASIFI_WHATSAPP_QR.SYNC_HINT')}`"
      :confirm-button-label="t('MASIFI_WHATSAPP_QR.UNLINK')"
      :cancel-button-label="t('MASIFI_WHATSAPP_QR.CANCEL')"
      @confirm="confirmUnlink"
    />
  </div>
</template>
